import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { validateRedirect } from "@njbsoft/utils";
import { authApiClient } from "@/lib/auth";
import { router } from "@/router";
import { useCompanyStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { MfaChallenge } from "@/components/mfa-challenge";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function extractPreferences(
  raw: unknown,
): { theme?: string; locale?: string; accent_color?: string } | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  return {
    theme: typeof obj.theme === "string" ? obj.theme : undefined,
    locale: typeof obj.locale === "string" ? obj.locale : undefined,
    accent_color: typeof obj.accent_color === "string" ? obj.accent_color : undefined,
  };
}

interface LoginSearch {
  redirect?: string;
}

/** Which invitation, if any, the visitor was bounced here from. */
type InviteContext = "tester" | "member" | null;

/**
 * The invite landings are the only routes whose whole payload rides the query
 * string, so their auth-wall bounce is the one arrival where a bare "Sign in to
 * NJBSoft" reads as a dead link: nothing on the page connects the mailed
 * invitation to the form in front of the visitor.
 *
 * Matched on the redirect's PATH alone, compared whole — a prefix test would
 * also claim any future `/accept-invite-…` route.
 */
function inviteContextFor(safeRedirect: string | null): InviteContext {
  if (safeRedirect === null) return null;
  const path = safeRedirect.split(/[?#]/)[0];
  if (path === "/accept-tester-invite") return "tester";
  if (path === "/accept-invite") return "member";
  return null;
}

/**
 * The tester invitation's token, lifted back out of the redirect it rides in
 * so `/signup` receives it as its own param. `/signup` is the authority on the
 * token's shape and drops anything that cannot be one, which is what keeps a
 * single gate rather than a second copy of it here.
 */
function testerTokenFrom(safeRedirect: string, invite: InviteContext): string | undefined {
  if (invite !== "tester") return undefined;
  return new URLSearchParams(safeRedirect.split("?")[1] ?? "").get("token") ?? undefined;
}

const HEADING_CLASS = "text-center text-3xl font-bold text-on-surface";
// Every control is a 44 px target below `sm`; from `sm`, its own height. A
// link's box is a 44 px square at least, whatever its text measures.
const INPUT_CLASS =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface max-sm:min-h-11";
const SUBMIT_CLASS =
  "w-full rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 max-sm:min-h-11";
const LINK_TARGET_CLASS =
  "max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center max-sm:justify-center";
const FORGOT_LINK_CLASS = `text-sm text-primary hover:text-primary-hover ${LINK_TARGET_CLASS}`;
const TEXT_LINK_CLASS = `font-medium text-primary hover:text-primary-hover ${LINK_TARGET_CLASS}`;
const SUBTITLE_CLASS = "mt-2 text-center text-sm text-muted";

// Every key is spelled at its own `t()` call site rather than picked into a
// variable, so the static key-resolution audit — which reads literal arguments
// — can see all six.
function SignInHeading({ invite }: { invite: InviteContext }) {
  const { t } = useTranslation(["auth", "company"]);

  if (invite === "tester") {
    return (
      <div>
        <h2 className={HEADING_CLASS}>{t("company:testerInvite.signInTitle")}</h2>
        <p className={SUBTITLE_CLASS}>{t("company:testerInvite.signInBody")}</p>
      </div>
    );
  }
  if (invite === "member") {
    return (
      <div>
        <h2 className={HEADING_CLASS}>{t("company:acceptInvite.signInTitle")}</h2>
        <p className={SUBTITLE_CLASS}>{t("company:acceptInvite.signInBody")}</p>
      </div>
    );
  }
  return (
    <div>
      <h2 className={HEADING_CLASS}>{t("auth:signInTitle")}</h2>
      <p className={SUBTITLE_CLASS}>{t("company:appTitle")}</p>
    </div>
  );
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation(["auth", "company", "common"]);
  const navigate = useNavigate();
  const setAuth = useCompanyStore((s) => s.setAuth);
  const setMfaChallenge = useCompanyStore((s) => s.setMfaChallenge);
  const clearMfaChallenge = useCompanyStore((s) => s.clearMfaChallenge);
  const mfaPending = useCompanyStore((s) => s.mfaPending);
  const { redirect } = Route.useSearch();
  // A redirect target is honored only when it is a safe in-app relative path
  // (rejects absolute and protocol-relative URLs to block open-redirect
  // phishing) — and the same validated value is what the heading branches on,
  // so a rejected target can never dress the page up as an invitation.
  const safeRedirect = validateRedirect(redirect);
  const invite = inviteContextFor(safeRedirect);
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const completeLogin = async (data: {
    access_token: string;
    refresh_token: string;
    expires_at: string;
    user: { id: string; email: string; role: string; email_verified: boolean };
    preferences?: { theme?: string; locale?: string; accent_color?: string };
  }) => {
    setAuth(
      data.access_token,
      {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        emailVerified: data.user.email_verified,
      },
      data.refresh_token,
    );
    if (data.expires_at) {
      useCompanyStore.getState().setExpiresAt(new Date(data.expires_at).getTime());
    }
    const prefs = data.preferences;
    if (prefs) {
      const theme = prefs.theme;
      if (theme === "system" || theme === "light" || theme === "dark") {
        useCompanyStore.getState().setThemePreference(theme);
      }
      const locale = prefs.locale;
      if (locale === "system" || locale === "en" || locale === "es") {
        useCompanyStore.getState().setLocalePreference(locale);
      }
      if (typeof prefs.accent_color === "string" && prefs.accent_color) {
        useCompanyStore.getState().setAccentColor(prefs.accent_color);
      }
    }
    clearMfaChallenge();
    await router.invalidate();
    // Default to "/" so the persona-derived landing picks the workspace — a
    // pure tester's first login must not land on the company dispatch board.
    void navigate({ to: safeRedirect ?? "/" });
  };

  const loginMutation = useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      const { data, error } = await authApiClient.POST("/auth/login", {
        body: creds,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      if (data.mfa_required && data.mfa_session_token && data.mfa_methods) {
        setMfaChallenge(data.mfa_session_token, data.mfa_methods);
        return;
      }
      if (data && data.access_token && data.refresh_token && data.user) {
        await completeLogin({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at ?? "",
          user: {
            id: data.user.id,
            email: data.user.email,
            role: data.user.role,
            email_verified: data.user.email_verified,
          },
          preferences: extractPreferences(data.user.preferences),
        });
      }
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      showApiError(code);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = (formData.get("email") as string).trim();
    const password = formData.get("password") as string;

    const errors: Record<string, string> = {};
    if (!email) {
      errors.email = t("common:validation.required");
    } else if (!EMAIL_RE.test(email)) {
      errors.email = t("common:validation.invalidEmail");
    }
    if (!password) {
      errors.password = t("common:validation.required");
    } else if (password.length < 8) {
      errors.password = t("common:validation.minLength", { min: 8 });
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    loginMutation.mutate({ email, password });
  };

  if (mfaPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <MfaChallenge
          onSuccess={(data) => void completeLogin(data)}
          onCancel={() => clearMfaChallenge()}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <SignInHeading invite={invite} />
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-muted">
                {t("emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={INPUT_CLASS}
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-muted">
                {t("passwordLabel")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className={INPUT_CLASS}
              />
              {fieldErrors.password && (
                <p className="mt-1 text-xs text-danger">{fieldErrors.password}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <Link to="/auth/forgot-password" className={FORGOT_LINK_CLASS}>
              {t("forgotPassword")}
            </Link>
          </div>
          <button type="submit" disabled={loginMutation.isPending} className={SUBMIT_CLASS}>
            {loginMutation.isPending ? t("signingIn") : t("signIn")}
          </button>
        </form>
        {/* Offered to invitees only: this portal is not a general registration
            funnel, and an invitee bounced here off a mailed link is the one
            visitor who may still need to create the account the invitation
            names. The whole redirect rides along, so the account lands back on
            the landing that sent them. */}
        {invite !== null && safeRedirect !== null && (
          <p className="text-center text-sm text-muted">
            {t("noAccount")}{" "}
            <Link
              to="/signup"
              search={{ redirect: safeRedirect, token: testerTokenFrom(safeRedirect, invite) }}
              className={TEXT_LINK_CLASS}
            >
              {t("createAccount")}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
